import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  Chip,
  Stack,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Tooltip,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Grid,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import RefreshIcon from '@mui/icons-material/Refresh';
import TerminalIcon from '@mui/icons-material/Terminal';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SearchIcon from '@mui/icons-material/Search';
import BuildIcon from '@mui/icons-material/Build';
import ReplayIcon from '@mui/icons-material/Replay';
import ScheduleSendIcon from '@mui/icons-material/ScheduleSend';

import { GameTaskLogResponse, TaskStage } from '../../types';
import { fetchGameTaskLogs, repairVideoFaststart, retryGameDownload, scheduleGameDownload } from '../../utils/apiClient';

interface TaskDebugLogsModalProps {
  isOpen: boolean;
  onClose: () => void;
  gameId: string;
  opponent?: string;
  date?: string;
}

export const TaskDebugLogsModal: React.FC<TaskDebugLogsModalProps> = ({
  isOpen,
  onClose,
  gameId,
  opponent,
  date,
}) => {
  const [logData, setLogData] = useState<GameTaskLogResponse | null>(null);
  const [selectedTaskId, setSelectedTaskId] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<boolean>(false);
  const [logFilter, setLogFilter] = useState<'ALL' | 'ERROR' | 'WARNING' | 'STAGE'>('ALL');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [repairing, setRepairing] = useState<boolean>(false);
  const [repairMessage, setRepairMessage] = useState<string | null>(null);
  const [retrying, setRetrying] = useState<boolean>(false);
  const [actionStatus, setActionStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const handleRetryFeed = async (feedType: string = 'pano') => {
    setRetrying(true);
    setActionStatus(null);
    try {
      const res = await retryGameDownload(gameId, feedType);
      const scheduledTime = res.scheduledFor ? new Date(res.scheduledFor).toLocaleTimeString() : '1 minute';
      setActionStatus({
        type: 'success',
        message: `Dispatched ${feedType.toUpperCase()} retry! Scheduled execution: ${scheduledTime} (Task: ${res.taskName || 'queued'}). Worker will automatically pick this up.`,
      });
      await loadLogs();
    } catch (err: any) {
      setActionStatus({
        type: 'error',
        message: `Failed to dispatch retry: ${err.message || 'Unknown error'}`,
      });
    } finally {
      setRetrying(false);
    }
  };

  const handleScheduleFull = async () => {
    setRetrying(true);
    setActionStatus(null);
    try {
      const res = await scheduleGameDownload(gameId, { opponent });
      const scheduledTime = res.scheduledFor ? new Date(res.scheduledFor).toLocaleTimeString() : '1 minute';
      setActionStatus({
        type: 'success',
        message: `Dispatched full download task! Scheduled execution: ${scheduledTime} (Task: ${res.taskName || 'queued'}).`,
      });
      await loadLogs();
    } catch (err: any) {
      setActionStatus({
        type: 'error',
        message: `Failed to schedule download: ${err.message || 'Unknown error'}`,
      });
    } finally {
      setRetrying(false);
    }
  };

  const loadLogs = async (taskIdToFetch?: string) => {
    if (!gameId) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchGameTaskLogs(gameId, taskIdToFetch || selectedTaskId || undefined);
      setLogData(data);
      if (data.taskId && (!selectedTaskId || !taskIdToFetch)) {
        setSelectedTaskId(data.taskId);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load task debug logs.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && gameId) {
      loadLogs();
    } else {
      setLogData(null);
      setSelectedTaskId('');
      setError(null);
      setRepairMessage(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, gameId]);

  const handleTaskChange = (newTaskId: string) => {
    setSelectedTaskId(newTaskId);
    loadLogs(newTaskId);
  };

  const handleRepairFaststart = async (feed: string = 'pano') => {
    setRepairing(true);
    setRepairMessage(null);
    try {
      const res = await repairVideoFaststart(gameId, feed);
      setRepairMessage(`Successfully repaired +faststart on ${res.feed} feed! (${(res.newSize / (1024 * 1024)).toFixed(1)} MB)`);
    } catch (err: any) {
      setRepairMessage(`Faststart repair failed: ${err.message}`);
    } finally {
      setRepairing(false);
    }
  };

  const logLines = useMemo(() => {
    if (!logData?.content) return [];
    return logData.content.split('\n');
  }, [logData?.content]);

  const filteredLogLines = useMemo(() => {
    return logLines.filter((line) => {
      if (logFilter === 'ERROR' && !line.includes('[ERROR]') && !line.includes('❌')) {
        return false;
      }
      if (logFilter === 'WARNING' && !line.includes('[WARNING') && !line.includes('⚠️')) {
        return false;
      }
      if (logFilter === 'STAGE' && !line.includes('Stage:') && !line.includes('▶️') && !line.includes('•')) {
        return false;
      }
      if (searchQuery.trim() && !line.toLowerCase().includes(searchQuery.toLowerCase().trim())) {
        return false;
      }
      return true;
    });
  }, [logLines, logFilter, searchQuery]);

  const handleCopyForAgent = () => {
    if (!logData) return;
    const summary = logData.summary || {};
    const isRunning = summary.status === 'processing' || summary.status === 'running';
    const activeTaskId = summary.activeTaskId || summary.lastTaskId || logData.taskId;
    const isShowingPreviousRun = isRunning || (logData.taskId && logData.taskId !== activeTaskId);

    const formattedReport = [
      `# LiveBarn Debug Log Report: ${gameId}`,
      `- Opponent: ${opponent || 'Unknown'}`,
      `- Date: ${date || 'Unknown'}`,
      `- Current Status: ${summary.status || 'unknown'}`,
      `- Current Stage: ${summary.currentStage || 'none'}`,
      `- Active Task ID: ${activeTaskId || 'none'}`,
      `- Displayed Log Task ID: ${logData.taskId || 'none'}${isShowingPreviousRun ? ' (Previous attempt transcript; active task is currently executing)' : ''}`,
      `- Last Heartbeat: ${summary.lastHeartbeat || 'none'}`,
      `- Delay Reason: ${summary.delayReason || 'none'}`,
      `- Scheduled Execution: ${summary.scheduledExecutionTime || 'none'}`,
      `- Last Error: ${summary.lastError || 'none'}`,
      `- GCS Log Path: ${logData.logGcsPath || 'none'}`,
      ...(isRunning ? [
        '',
        '> [!NOTE]',
        '> An active background task is currently running. Full logs will be written to GCS upon completion. The transcript below reflects the previous execution.',
      ] : []),
      '',
      '## Raw Execution Transcript',
      '```',
      logData.content || 'No log content.',
      '```',
    ].join('\n');

    navigator.clipboard.writeText(formattedReport);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const summary = logData?.summary || {};
  const stages: TaskStage[] = summary.taskStages || [];
  const metrics = summary.taskMetrics || {};

  const getStatusColor = (st?: string): 'success' | 'info' | 'warning' | 'error' | 'secondary' => {
    if (!st) return 'secondary';
    if (st === 'complete' || st === 'ready') return 'success';
    if (st === 'processing' || st === 'running') return 'info';
    if (st === 'scheduled' || st === 'game_conflict_delayed') return 'secondary';
    if (st === 'overrun_delayed') return 'warning';
    return 'error';
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#0f172a',
          color: '#f8fafc',
          borderRadius: 2,
          border: '1px solid #334155',
        },
      }}
    >
      {/* Dialog Header */}
      <DialogTitle sx={{ m: 0, p: 2.5, pb: 1.5, borderBottom: '1px solid #1e293b' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                p: 1,
                bgcolor: 'rgba(37, 99, 235, 0.15)',
                color: '#60a5fa',
                borderRadius: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <TerminalIcon />
            </Box>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h6" component="span" fontWeight={600}>
                  Video Task Debug Logs
                </Typography>
                {summary.status && (
                  <Chip
                    label={summary.status.toUpperCase()}
                    size="small"
                    color={getStatusColor(summary.status)}
                    sx={{ fontWeight: 600, fontSize: '0.7rem', height: 20 }}
                  />
                )}
              </Stack>
              <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                {gameId} {opponent ? `• vs ${opponent}` : ''} {date ? `• ${date}` : ''}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Refresh logs from Google Cloud Storage">
              <IconButton
                onClick={() => loadLogs()}
                disabled={isLoading}
                size="small"
                sx={{ color: '#94a3b8', '&:hover': { color: '#f8fafc' } }}
              >
                {isLoading ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{ color: '#94a3b8', '&:hover': { color: '#f8fafc' } }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>
      </DialogTitle>

      {/* Dialog Content */}
      <DialogContent sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {/* Controls Bar: Task Selector + Actions */}
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            bgcolor: 'rgba(15, 23, 42, 0.6)',
            borderColor: '#334155',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="task-select-label" sx={{ color: '#94a3b8' }}>
                Execution Run
              </InputLabel>
              <Select
                labelId="task-select-label"
                value={selectedTaskId}
                label="Execution Run"
                onChange={(e) => handleTaskChange(e.target.value)}
                disabled={isLoading || !(logData?.availableLogs?.length)}
                sx={{
                  bgcolor: '#1e293b',
                  color: '#f8fafc',
                  '.MuiOutlinedInput-notchedOutline': { borderColor: '#475569' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#64748b' },
                  fontSize: '0.8rem',
                }}
              >
                {logData?.availableLogs && logData.availableLogs.length > 0 ? (
                  logData.availableLogs.map((log) => (
                    <MenuItem key={log.taskId} value={log.taskId} sx={{ fontSize: '0.8rem' }}>
                      {log.taskId} {log.updatedAt ? `(${new Date(log.updatedAt).toLocaleTimeString()})` : ''}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="" sx={{ fontSize: '0.8rem' }}>
                    {logData?.taskId || 'Current Execution'}
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            {logData?.logGcsPath && (
              <Typography variant="caption" sx={{ color: '#64748b', fontFamily: 'monospace' }}>
                GCS: {logData.logGcsPath}
              </Typography>
            )}
          </Stack>

          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              color="warning"
              startIcon={retrying ? <CircularProgress size={14} color="inherit" /> : <ReplayIcon />}
              onClick={() => handleRetryFeed('pano')}
              disabled={retrying || repairing}
              sx={{ textTransform: 'none', fontSize: '0.75rem', fontWeight: 600 }}
            >
              {retrying ? 'Queueing Retry...' : 'Retry Pano Download'}
            </Button>

            <Button
              size="small"
              variant="outlined"
              color="info"
              startIcon={retrying ? <CircularProgress size={14} color="inherit" /> : <ScheduleSendIcon />}
              onClick={handleScheduleFull}
              disabled={retrying || repairing}
              sx={{ textTransform: 'none', fontSize: '0.75rem' }}
            >
              Re-queue Task
            </Button>

            <Button
              size="small"
              variant="outlined"
              color="warning"
              startIcon={repairing ? <CircularProgress size={14} color="inherit" /> : <BuildIcon />}
              onClick={() => handleRepairFaststart('pano')}
              disabled={repairing || retrying}
              sx={{ textTransform: 'none', fontSize: '0.75rem' }}
            >
              {repairing ? 'Repairing Pano...' : 'Repair Pano (+faststart)'}
            </Button>

            <Button
              size="small"
              variant="contained"
              color={copied ? 'success' : 'primary'}
              startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
              onClick={handleCopyForAgent}
              sx={{ textTransform: 'none', fontSize: '0.75rem', fontWeight: 600 }}
            >
              {copied ? 'Copied for Agent!' : 'Copy for AI Agent'}
            </Button>
          </Stack>
        </Paper>

        {actionStatus && (
          <Alert
            severity={actionStatus.type}
            onClose={() => setActionStatus(null)}
            sx={{
              bgcolor: actionStatus.type === 'success' ? 'rgba(5, 150, 105, 0.15)' : 'rgba(225, 29, 72, 0.15)',
              color: actionStatus.type === 'success' ? '#a7f3d0' : '#fecdd3',
              border: `1px solid ${actionStatus.type === 'success' ? '#059669' : '#e11d48'}`,
            }}
          >
            {actionStatus.message}
          </Alert>
        )}

        {repairMessage && (
          <Alert severity="info" sx={{ bgcolor: 'rgba(2, 136, 209, 0.15)', color: '#bae6fd', border: '1px solid #0288d1' }}>
            {repairMessage}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ bgcolor: 'rgba(225, 29, 72, 0.15)', color: '#fecdd3', border: '1px solid #e11d48' }}>
            {error}
          </Alert>
        )}

        {(summary.status === 'processing' || summary.status === 'running') && (
          <Alert
            severity="info"
            sx={{
              bgcolor: 'rgba(37, 99, 235, 0.15)',
              color: '#93c5fd',
              border: '1px solid #2563eb',
            }}
          >
            <Typography variant="body2" fontWeight={600}>
              Video task is currently running ({summary.currentStage || 'processing'}).
            </Typography>
            <Typography variant="caption" sx={{ color: '#bfdbfe' }}>
              Execution transcripts are finalized to Google Cloud Storage when the task completes. The transcript displayed below is from the latest saved attempt ({logData?.taskId || 'previous run'}).
            </Typography>
          </Alert>
        )}

        {/* Pipeline Stages Visual Stepper */}
        {stages && stages.length > 0 && (
          <Box>
            <Typography variant="caption" fontWeight={700} sx={{ color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Pipeline Stages
            </Typography>
            <Grid container spacing={1} sx={{ mt: 0.5 }}>
              {stages.map((stage, idx) => {
                const isDone = stage.status === 'completed';
                const isFail = stage.status === 'failed';
                const isCur = stage.status === 'in_progress';
                return (
                  <Grid size={{ xs: 6, sm: 4, md: 2.4 }} key={idx}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.2,
                        borderRadius: 1.5,
                        borderColor: isFail ? '#e11d48' : isDone ? '#059669' : isCur ? '#2563eb' : '#334155',
                        bgcolor: isFail ? 'rgba(225, 29, 72, 0.1)' : isDone ? 'rgba(5, 150, 105, 0.1)' : isCur ? 'rgba(37, 99, 235, 0.15)' : 'rgba(30, 41, 59, 0.5)',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        minHeight: 70,
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" fontWeight={700} sx={{ textTransform: 'capitalize', color: '#f8fafc' }} noWrap>
                          {stage.name}
                        </Typography>
                        {isDone ? (
                          <CheckCircleOutlineIcon sx={{ fontSize: 14, color: '#34d399' }} />
                        ) : isFail ? (
                          <ErrorOutlineIcon sx={{ fontSize: 14, color: '#fb7185' }} />
                        ) : (
                          <AccessTimeIcon sx={{ fontSize: 14, color: '#60a5fa' }} />
                        )}
                      </Box>
                      <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.65rem' }} noWrap>
                        {stage.description}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                        <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.65rem' }}>
                          {stage.durationSeconds !== null ? `${stage.durationSeconds}s` : 'running'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: isDone ? '#34d399' : isFail ? '#fb7185' : '#60a5fa', fontSize: '0.65rem', fontWeight: 600, textTransform: 'capitalize' }}>
                          {stage.status}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}

        {/* Telemetry Metric Summary Cards */}
        <Grid container spacing={1.5}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#020617', borderColor: '#1e293b' }}>
              <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', fontSize: '0.65rem', fontWeight: 700 }}>
                Current Stage
              </Typography>
              <Typography variant="body2" fontWeight={600} sx={{ color: '#f8fafc', textTransform: 'capitalize' }}>
                {summary.currentStage || 'None'}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#020617', borderColor: '#1e293b' }}>
              <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', fontSize: '0.65rem', fontWeight: 700 }}>
                Delay / Overrun
              </Typography>
              <Typography variant="body2" fontWeight={600} sx={{ color: summary.delayReason ? '#f59e0b' : '#f8fafc' }} noWrap title={summary.delayReason || 'None'}>
                {summary.delayReason || 'None'}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#020617', borderColor: '#1e293b' }}>
              <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', fontSize: '0.65rem', fontWeight: 700 }}>
                Last Heartbeat
              </Typography>
              <Typography variant="body2" fontWeight={600} sx={{ color: '#f8fafc' }} noWrap>
                {summary.lastHeartbeat ? new Date(summary.lastHeartbeat).toLocaleTimeString() : 'N/A'}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#020617', borderColor: '#1e293b' }}>
              <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', fontSize: '0.65rem', fontWeight: 700 }}>
                Scheduled Execution
              </Typography>
              <Typography variant="body2" fontWeight={600} sx={{ color: '#f8fafc' }} noWrap>
                {summary.scheduledExecutionTime ? new Date(summary.scheduledExecutionTime).toLocaleString() : 'N/A'}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Telemetry Metrics Details */}
        {Object.keys(metrics).length > 0 && (
          <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#020617', borderColor: '#1e293b' }}>
            <Typography variant="caption" fontWeight={700} sx={{ color: '#64748b', textTransform: 'uppercase', fontSize: '0.65rem', display: 'block', mb: 1 }}>
              Extraction Metrics & Markers
            </Typography>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
              {Object.entries(metrics).map(([k, v]) => (
                <Chip
                  key={k}
                  label={`${k}: ${String(v)}`}
                  size="small"
                  sx={{ bgcolor: '#1e293b', color: '#cbd5e1', fontSize: '0.7rem', border: '1px solid #334155' }}
                />
              ))}
            </Stack>
          </Paper>
        )}

        {/* Raw Terminal Logs Section */}
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="caption" fontWeight={700} sx={{ color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Execution Transcript ({filteredLogLines.length} lines)
            </Typography>

            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                size="small"
                placeholder="Filter logs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: '#64748b' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: 180,
                  bgcolor: '#020617',
                  '& .MuiInputBase-input': { color: '#f8fafc', fontSize: '0.75rem', py: 0.5 },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#334155' },
                }}
              />

              <ToggleButtonGroup
                value={logFilter}
                exclusive
                onChange={(_, val) => val && setLogFilter(val)}
                size="small"
                sx={{
                  bgcolor: '#1e293b',
                  '& .MuiToggleButton-root': {
                    color: '#94a3b8',
                    py: 0.25,
                    px: 1,
                    fontSize: '0.7rem',
                    textTransform: 'none',
                    borderColor: '#334155',
                    '&.Mui-selected': { bgcolor: '#2563eb', color: '#fff' },
                  },
                }}
              >
                <ToggleButton value="ALL">All</ToggleButton>
                <ToggleButton value="ERROR">Errors</ToggleButton>
                <ToggleButton value="WARNING">Warnings</ToggleButton>
                <ToggleButton value="STAGE">Stages</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Box>

          <Box
            sx={{
              p: 2,
              bgcolor: '#020617',
              borderRadius: 1.5,
              border: '1px solid #1e293b',
              fontFamily: 'monospace',
              fontSize: '0.75rem',
              lineHeight: 1.6,
              color: '#cbd5e1',
              maxHeight: 360,
              overflowY: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              userSelect: 'text',
            }}
          >
            {isLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2, color: '#94a3b8' }}>
                <CircularProgress size={16} color="inherit" />
                <span>Loading transcript from Google Cloud Storage...</span>
              </Box>
            ) : filteredLogLines.length === 0 ? (
              <Typography variant="body2" sx={{ color: '#64748b', fontStyle: 'italic', py: 2 }}>
                No matching log entries found.
              </Typography>
            ) : (
              filteredLogLines.map((line, idx) => {
                let color = '#cbd5e1';
                let fontWeight = 400;
                if (line.includes('[ERROR]') || line.includes('❌') || line.includes('Error:')) {
                  color = '#fb7185';
                  fontWeight = 600;
                } else if (line.includes('[WARNING') || line.includes('⚠️')) {
                  color = '#fcd34d';
                } else if (line.includes('▶️') || line.includes('Stage:')) {
                  color = '#67e8f9';
                  fontWeight = 500;
                } else if (line.includes('🏁') || line.includes('finished with status')) {
                  color = '#6ee7b7';
                  fontWeight = 600;
                } else if (line.startsWith('=')) {
                  color = '#475569';
                }

                return (
                  <Box key={idx} sx={{ color, fontWeight, '&:hover': { bgcolor: 'rgba(30, 41, 59, 0.5)' }, px: 0.5, borderRadius: 0.5 }}>
                    {line}
                  </Box>
                );
              })
            )}
          </Box>
        </Box>
      </DialogContent>

      {/* Dialog Actions / Footer */}
      <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: '1px solid #1e293b', justifyContent: 'space-between' }}>
        <Typography variant="caption" sx={{ color: '#64748b' }}>
          Logs stored at <code style={{ color: '#94a3b8' }}>gs://chicagojets2014-videos/games/{gameId}/logs/</code>
        </Typography>
        <Button onClick={onClose} variant="outlined" size="small" sx={{ color: '#cbd5e1', borderColor: '#475569' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
